/**
 * Track15 Nexus Terminology Configuration
 *
 * Centralized terminology constants for consistent naming across the platform.
 * This enables:
 * - Consistent UI language throughout the application
 * - Easy migration from "Client" to "Workspace" terminology
 * - Future white-label customization support
 */

export const TERMINOLOGY = {
  /**
   * Workspace terminology (formerly "Client")
   * Each organization operates within their own workspace
   */
  workspace: {
    singular: "Workspace",
    plural: "Workspaces",
    article: "a workspace",
    possessive: "workspace's",
    select: "Select a Workspace",
    selectDescription: "Choose an organization workspace to continue.",
    search: "Search workspaces...",
    viewAll: "View all workspaces",
    noAccess: "You don't have access to any workspaces yet.",
    loading: "Loading your workspaces...",
    switch: "Switch workspace",
    current: "Current workspace",
  },

  /**
   * Platform identity
   */
  platform: {
    name: "Track15 Nexus",
    company: "Track15",
    tagline: "The Command Center for Fundraising Strategy",
    version: "0.3.0",
    poweredBy: "Powered by Track15 Nexus",
    copyright: `© ${new Date().getFullYear()} Track15`,
  },

  /**
   * AI Assistant branding
   */
  assistant: {
    name: "Ask Nexus",
    subtitle: "Track15 Intelligence Assistant",
    greeting: "How can I help with your fundraising strategy?",
    placeholder: "Ask about donors, campaigns, or strategy...",
    thinking: "Analyzing...",
    error: "I couldn't process that request. Please try again.",
  },

  /**
   * Navigation labels
   */
  navigation: {
    home: "Home",
    decisionCockpit: "Decision Cockpit",
    donors: "Donors",
    campaigns: "Campaigns",
    journeys: "Journeys",
    segments: "Segments",
    analytics: "Analytics",
    reports: "Reports",
    settings: "Settings",
    admin: "Admin",
  },

  /**
   * Report branding
   */
  reports: {
    generatedBy: "Generated by Track15 Nexus",
    subtitle: "Fundraising Intelligence Platform",
    preparedFor: "Prepared for",
  },
} as const;

// Type exports for TypeScript inference
export type WorkspaceTerms = typeof TERMINOLOGY.workspace;
export type PlatformTerms = typeof TERMINOLOGY.platform;
export type AssistantTerms = typeof TERMINOLOGY.assistant;
export type NavigationTerms = typeof TERMINOLOGY.navigation;
export type ReportTerms = typeof TERMINOLOGY.reports;
